<script setup>
import { computed } from 'vue'
import moment from 'moment'

// Persistent strip shown under the search bar while the view is scoped to
// results ingested since the user's watermark (the "view new results" flow).
// It names the scope in plain language — the event-time dropdown still reads
// "All time" because this bounds a different axis (when data arrived, not when
// the event happened) — and offers the single action to leave the mode.
const props = defineProps({
  since: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['clear'])

const absolute = computed(() => moment(props.since).format('MMM D, YYYY · h:mm A'))
const relative = computed(() => moment(props.since).fromNow())
</script>

<template>
  <div class="new-results-strip">
    <font-awesome-icon icon="wand-magic-sparkles" class="nr-icon" />
    <div class="nr-text">
      <span class="nr-title">New results since {{ absolute }}</span>
      <span class="nr-sub">· {{ relative }} — events ingested since you last opened this search</span>
    </div>
    <button class="nr-clear" @click="emit('clear')">
      Show all results
      <font-awesome-icon icon="arrow-right" />
    </button>
  </div>
</template>

<style scoped>
.new-results-strip {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.625rem 1rem;
  background: var(--accent-soft-2);
  border: 1px solid var(--accent-soft-3);
  border-radius: var(--radius-xl);
  color: var(--text-primary);
  font-size: 0.8125rem;
}

.nr-icon {
  color: var(--accent-text);
  font-size: 0.875rem;
  flex-shrink: 0;
}

.nr-text {
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.nr-title {
  font-weight: 600;
}

.nr-sub {
  color: var(--text-muted);
}

.nr-clear {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  background: var(--accent-soft-3);
  border: 1px solid var(--accent-soft-4);
  border-radius: var(--radius-md);
  color: var(--accent-text);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.nr-clear:hover {
  background: var(--accent-soft-4);
  border-color: var(--accent-soft-5);
}
</style>
